import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  Hash,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  GripVertical,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Users,
  UserCog,
  Shield,
  Loader2,
  AlertCircle
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

import { API_URL } from "@/utils/api";
const API = API_URL;

const ROLE_OPTIONS = [
  { value: "standard", label: "Standard Users", icon: Users },
  { value: "phone_admin", label: "Phone Admins", icon: UserCog },
  { value: "customer_admin", label: "Customer Admins", icon: Shield },
];

const CATEGORY_OPTIONS = [
  "Voicemail",
  "Forwarding",
  "DND & Privacy",
  "Call Queue",
  "Recording",
  "Call Parking",
  "Pickup",
  "Utilities",
  "Auto Attendant",
  "Conferencing",
  "Hot Desk",
  "Other"
];

const DEFAULT_CODE = {
  code: "",
  name: "",
  description: "",
  category: "Other",
  requires_input: false,
  input_placeholder: "Number",
  visible_to: ["standard", "phone_admin", "customer_admin"]
};

export function StarCodesManager() {
  const { token } = useAuth();
  
  const [starCodes, setStarCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [isNewCode, setIsNewCode] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  
  // Expanded categories
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  useEffect(() => {
    fetchStarCodes();
  }, [token]);
  
  const fetchStarCodes = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/star-codes/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(response.data.codes || []);
      
      // Get unique categories
      const cats = [...new Set((response.data.codes || []).map(c => c.category))];
      setCategories(cats.length > 0 ? cats : CATEGORY_OPTIONS);
      
      // Expand all categories by default
      setExpandedCategories(cats);
    } catch (error) {
      console.error("Failed to fetch star codes:", error);
      if (error.response?.status === 404) {
        // No codes yet
        setStarCodes([]);
        setCategories(CATEGORY_OPTIONS);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const initializeDefaults = async () => {
    try {
      setSaving(true);
      const response = await axios.post(`${API}/star-codes/initialize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(response.data.codes || []);
      
      const cats = [...new Set((response.data.codes || []).map(c => c.category))];
      setCategories(cats);
      setExpandedCategories(cats);
      
      toast.success("Star codes initialized with provider defaults");
    } catch (error) {
      console.error("Failed to initialize:", error);
      toast.error("Failed to initialize star codes");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm("This will replace your current star codes with the provider defaults. Any custom codes will be lost. Continue?")) {
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(`${API}/star-codes/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(response.data.codes || []);
      
      const cats = [...new Set((response.data.codes || []).map(c => c.category))];
      setCategories(cats);
      setExpandedCategories(cats);
      
      toast.success("Star codes reset to provider defaults");
    } catch (error) {
      console.error("Failed to reset:", error);
      toast.error("Failed to reset star codes");
    } finally {
      setSaving(false);
    }
  };
  
  const saveAllCodes = async (codes) => {
    try {
      setSaving(true);
      await axios.put(`${API}/star-codes`, { codes }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarCodes(codes);
      toast.success("Star codes saved");
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("Failed to save star codes");
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddCode = () => {
    setEditingCode({ ...DEFAULT_CODE });
    setIsNewCode(true);
    setEditDialogOpen(true);
  };
  
  const handleEditCode = (code) => {
    setEditingCode({ ...code });
    setIsNewCode(false);
    setEditDialogOpen(true);
  };
  
  const handleSaveCode = () => {
    if (!editingCode.code.trim() || !editingCode.name.trim()) {
      toast.error("Code and name are required");
      return;
    }
    
    let updatedCodes;
    if (isNewCode) {
      updatedCodes = [...starCodes, { ...editingCode, id: crypto.randomUUID() }];
    } else {
      updatedCodes = starCodes.map(c => 
        c.id === editingCode.id ? editingCode : c
      );
    }
    
    saveAllCodes(updatedCodes);
    setEditDialogOpen(false);
    
    // Update categories if new one added
    if (!categories.includes(editingCode.category)) {
      setCategories([...categories, editingCode.category]);
    }
  };
  
  const handleDeleteCode = (code) => {
    setCodeToDelete(code);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = () => {
    if (!codeToDelete) return;
    
    const updatedCodes = starCodes.filter(c => c.id !== codeToDelete.id);
    saveAllCodes(updatedCodes);
    setDeleteDialogOpen(false);
    setCodeToDelete(null);
  };
  
  const toggleRoleVisibility = (role) => {
    if (!editingCode) return;
    
    const currentRoles = editingCode.visible_to || [];
    const newRoles = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    
    setEditingCode({ ...editingCode, visible_to: newRoles });
  };
  
  // Group codes by category
  const codesByCategory = categories.reduce((acc, cat) => {
    acc[cat] = starCodes.filter(c => c.category === cat);
    return acc;
  }, {});
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <Card className="border-border/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-cyan-500" />
              Star Codes
            </CardTitle>
            <CardDescription>
              Configure star codes available to your team from the dialer
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {starCodes.length === 0 ? (
              <Button onClick={initializeDefaults} disabled={saving}>
                <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                Load Defaults
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={resetToDefaults} disabled={saving}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                  Copy from Defaults
                </Button>
                <Button onClick={handleAddCode}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Code
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {starCodes.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Hash className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No star codes configured</h3>
            <p className="text-muted-foreground mb-4">
              Load the default codes for your PBX provider or add custom codes
            </p>
            <Button onClick={initializeDefaults} disabled={saving}>
              <RefreshCw className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
              Load Provider Defaults
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>{starCodes.length} star codes configured</span>
              <span>{categories.length} categories</span>
            </div>
            
            <Accordion
              type="multiple"
              value={expandedCategories}
              onValueChange={setExpandedCategories}
              className="space-y-2"
            >
              {categories.map((category) => {
                const codes = codesByCategory[category] || [];
                if (codes.length === 0) return null;
                
                return (
                  <AccordionItem
                    key={category}
                    value={category}
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="py-3 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">{codes.length}</Badge>
                        <span className="font-medium">{category}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2 pb-2">
                        {codes.map((code) => (
                          <div
                            key={code.id}
                            className="flex items-center gap-3 p-3 rounded-md bg-secondary/30 hover:bg-secondary/50 group"
                          >
                            <div className="w-20 font-mono text-sm text-cyan-500 font-medium">
                              {code.code}
                              {code.requires_input && <span className="text-muted-foreground">...</span>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{code.name}</div>
                              {code.description && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {code.description}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              {code.visible_to?.map(role => (
                                <Badge key={role} variant="outline" className="text-[10px] px-1">
                                  {role === "standard" ? "Std" : role === "phone_admin" ? "PA" : "CA"}
                                </Badge>
                              ))}
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCode(code)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteCode(code)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}
      </CardContent>
      
      {/* Edit/Add Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5 text-cyan-500" />
              {isNewCode ? "Add Star Code" : "Edit Star Code"}
            </DialogTitle>
            <DialogDescription>
              Configure the star code details and visibility
            </DialogDescription>
          </DialogHeader>
          
          {editingCode && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Code *</Label>
                  <Input
                    id="code"
                    placeholder="e.g. *72"
                    value={editingCode.code}
                    onChange={(e) => setEditingCode({ ...editingCode, code: e.target.value })}
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={editingCode.category}
                    onValueChange={(v) => setEditingCode({ ...editingCode, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g. Forward All Calls"
                  value={editingCode.name}
                  onChange={(e) => setEditingCode({ ...editingCode, name: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  placeholder="Brief description of what this code does"
                  value={editingCode.description || ""}
                  onChange={(e) => setEditingCode({ ...editingCode, description: e.target.value })}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 rounded-md bg-secondary/30">
                <div>
                  <Label className="text-sm font-medium">Requires Input</Label>
                  <p className="text-xs text-muted-foreground">
                    Code needs a number to be entered after it (e.g. *72 + phone number)
                  </p>
                </div>
                <Switch
                  checked={editingCode.requires_input || false}
                  onCheckedChange={(v) => setEditingCode({ ...editingCode, requires_input: v })}
                />
              </div>
              
              {editingCode.requires_input && (
                <div className="space-y-2">
                  <Label htmlFor="placeholder">Input Placeholder</Label>
                  <Input
                    id="placeholder"
                    placeholder="e.g. Forward to number"
                    value={editingCode.input_placeholder || ""}
                    onChange={(e) => setEditingCode({ ...editingCode, input_placeholder: e.target.value })}
                  />
                </div>
              )}
              
              <div className="space-y-3">
                <Label>Visible To</Label>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map(({ value, label, icon: Icon }) => (
                    <div
                      key={value}
                      className="flex items-center justify-between p-3 rounded-md bg-secondary/30"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{label}</span>
                      </div>
                      <Checkbox
                        checked={(editingCode.visible_to || []).includes(value)}
                        onCheckedChange={() => toggleRoleVisibility(value)}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Users will only see codes they have access to
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCode} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Star Code?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <strong>{codeToDelete?.code}</strong> ({codeToDelete?.name}) from your star codes.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default StarCodesManager;
